// Hotel-Edition "Themenkatalog" (PLAN.md §6.2, part C). The hotel version of /instagram-saeulen: the topics
// the Signal Room imported (instagram_pillars) in hotel language with their rhythm and an "Aktiv" switch
// (the existing hide-pref), plus "Themenwunsch hinzufügen" → POST /api/hotel/topic-wishes (table + mail to the
// team) and the list "Ihre Wünsche" with status. No dates yet (Mike C1: until the Signal Room exports them).
// Reachable only with the 'themen' module (EditionModuleGate in App.tsx); the APIs check it again server-side.
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, Check, Loader2, Plus } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { ContentUpload } from "@shared/schema";

type TopicRow = {
  id: string;
  rank: number;
  name: string;
  normalizedName: string;
  claim: string | null;
  cadence: string | null;
  series: string | null;
  seedIdeasJson: unknown[];
  hidden: boolean;
};

type WishStatus = "neu" | "beim_team" | "eingeplant" | "abgelehnt";
type Wish = {
  id: string;
  topic: string;
  period: string | null;
  note: string | null;
  photoUploadIds: string[];
  status: WishStatus;
  createdAt: string;
};

const TOPICS_KEY = ["/api/signal-room/instagram-pillars"];
const WISHES_KEY = ["/api/hotel/topic-wishes"];
const PHOTOS_KEY = ["/api/uploads/my-approved"];
const MAX_PHOTOS = 10;

export const WISH_STATUS_LABEL: Record<WishStatus, { label: string; className: string }> = {
  neu: { label: "Neu", className: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-200" },
  beim_team: { label: "Bei Ihrem Team", className: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300" },
  eingeplant: { label: "Eingeplant", className: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300" },
  abgelehnt: { label: "Nicht eingeplant", className: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300" },
};

/** Imported jsonb is exporter-shaped; never render "[object Object]". */
function asText(entry: unknown): string {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    const o = entry as Record<string, unknown>;
    const parts = [o.title, o.angle, o.text].filter((x) => typeof x === "string" && x);
    if (parts.length) return parts.join(" · ");
  }
  return "";
}

function newRequestKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function Themenkatalog() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [formOpen, setFormOpen] = useState(false);

  const topics = useQuery<{ mapped: boolean; pillars: TopicRow[] }>({ queryKey: TOPICS_KEY, staleTime: 0, refetchOnMount: "always" });
  const wishes = useQuery<{ wishes: Wish[] }>({ queryKey: WISHES_KEY });

  const toggleActive = useMutation({
    mutationFn: ({ normalizedName, hidden }: { normalizedName: string; hidden: boolean }) =>
      apiRequest("POST", "/api/signal-room/instagram-pillars/prefs", { normalizedName, hidden }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TOPICS_KEY }),
    onError: (err: any) => toast({
      title: "Änderung fehlgeschlagen",
      description: err?.data?.error || "Bitte versuchen Sie es erneut.",
      variant: "destructive",
    }),
  });

  const rows = topics.data?.pillars ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="page-themenkatalog">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <BookOpen className="h-8 w-8 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-bold">Themenkatalog</h1>
            <p className="text-sm text-muted-foreground">
              Die Themen, über die Ihr Haus regelmäßig erzählt. Ihr Team plant daraus Ihre Beiträge.
            </p>
          </div>
        </div>
        <Button onClick={() => setFormOpen((o) => !o)} className="gap-2" data-testid="button-add-topic-wish">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Themenwunsch hinzufügen
        </Button>
      </div>

      {formOpen && <TopicWishForm onDone={() => setFormOpen(false)} topicNames={rows.map((r) => r.name)} />}

      <section className="space-y-3" aria-labelledby="themen-heading">
        <h2 id="themen-heading" className="text-lg font-semibold">Ihre Themen</h2>
        {topics.isLoading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
        ) : topics.isError ? (
          <Card><CardContent className="p-6 text-muted-foreground">Die Themen konnten nicht geladen werden. Bitte laden Sie die Seite neu.</CardContent></Card>
        ) : !topics.data?.mapped || rows.length === 0 ? (
          <Card data-testid="card-themen-empty">
            <CardContent className="p-8 text-center text-muted-foreground">
              Ihr Themenkatalog wird gerade von Ihrem Team vorbereitet. Die Themen erscheinen hier, sobald sie fertig sind.
            </CardContent>
          </Card>
        ) : (
          rows.map((t) => {
            const isOpen = !!expanded[t.id];
            const ideas = (t.seedIdeasJson ?? []).map(asText).filter(Boolean);
            return (
              <Card key={t.id} className={t.hidden ? "opacity-60" : ""} data-testid={`card-thema-${t.rank}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      className="mt-1 text-muted-foreground"
                      onClick={() => setExpanded((e) => ({ ...e, [t.id]: !isOpen }))}
                      aria-label={isOpen ? "Ideen ausblenden" : "Ideen anzeigen"}
                      aria-expanded={isOpen}
                      data-testid={`button-thema-expand-${t.rank}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{t.name}</span>
                        {t.cadence && <Badge variant="outline" className="text-xs">Rhythmus: {t.cadence}</Badge>}
                      </div>
                      {t.claim && <p className="text-sm text-muted-foreground mt-1">{t.claim}</p>}
                      {isOpen && (
                        <div className="mt-3 space-y-2 text-sm">
                          {t.series && <div className="text-muted-foreground">Reihe: {t.series}</div>}
                          {ideas.length > 0 ? (
                            <div>
                              <div className="font-medium mb-1">Ideen für Beiträge</div>
                              <ul className="list-disc pl-5 space-y-1">{ideas.map((idea, i) => <li key={i}>{idea}</li>)}</ul>
                            </div>
                          ) : (
                            <div className="text-muted-foreground">Noch keine Ideen hinterlegt.</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">Aktiv</span>
                      <Switch
                        checked={!t.hidden}
                        onCheckedChange={(active) => toggleActive.mutate({ normalizedName: t.normalizedName, hidden: !active })}
                        aria-label={`Thema ${t.name} aktiv`}
                        data-testid={`switch-thema-active-${t.rank}`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </section>

      <section className="space-y-3" aria-labelledby="wuensche-heading">
        <h2 id="wuensche-heading" className="text-lg font-semibold">Ihre Wünsche</h2>
        {wishes.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : wishes.isError ? (
          <Card><CardContent className="p-6 text-muted-foreground">Ihre Wünsche konnten nicht geladen werden.</CardContent></Card>
        ) : (wishes.data?.wishes ?? []).length === 0 ? (
          <Card data-testid="card-wishes-empty">
            <CardContent className="p-6 text-muted-foreground">
              Noch keine Wünsche. Haben Sie ein Thema, ein Ereignis oder ein Angebot, das in Ihre Beiträge soll? Fügen Sie einen Themenwunsch hinzu.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {wishes.data!.wishes.map((w) => {
              const st = WISH_STATUS_LABEL[w.status] ?? WISH_STATUS_LABEL.neu;
              return (
                <Card key={w.id} data-testid={`card-wish-${w.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">{w.topic}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {w.period ? `Zeitraum: ${w.period} · ` : ""}
                        Gewünscht am {format(new Date(w.createdAt), "dd.MM.yyyy", { locale: de })}
                        {w.photoUploadIds.length > 0 ? ` · ${w.photoUploadIds.length} ${w.photoUploadIds.length === 1 ? "Foto" : "Fotos"}` : ""}
                      </div>
                      {w.note && <p className="text-sm text-muted-foreground mt-1">{w.note}</p>}
                    </div>
                    <Badge className={`text-xs border ${st.className}`} data-testid={`badge-wish-status-${w.id}`}>{st.label}</Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function TopicWishForm({ onDone, topicNames }: { onDone: () => void; topicNames: string[] }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [topic, setTopic] = useState("");
  const [period, setPeriod] = useState("");
  const [note, setNote] = useState("");
  const [photoIds, setPhotoIds] = useState<string[]>([]);
  // One key per form: a double click or a retry after a lost answer is the same wish (server idempotent).
  const requestKey = useRef(newRequestKey());

  const photos = useQuery<ContentUpload[]>({ queryKey: PHOTOS_KEY });
  const photoList = useMemo(() => (photos.data ?? []).slice(0, 40), [photos.data]);

  const submit = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/hotel/topic-wishes", {
        topic: topic.trim(),
        period: period.trim() || undefined,
        note: note.trim() || undefined,
        photoUploadIds: photoIds,
        requestKey: requestKey.current,
      }) as Promise<{ message: string }>;
    },
    onSuccess: (data) => {
      toast({ title: "Wunsch gespeichert", description: data?.message });
      queryClient.invalidateQueries({ queryKey: WISHES_KEY });
      onDone();
    },
    onError: (err: any) => toast({
      title: "Wunsch nicht gespeichert",
      description: err?.data?.error || "Bitte versuchen Sie es in einigen Minuten erneut.",
      variant: "destructive",
    }),
  });

  const togglePhoto = (id: string) =>
    setPhotoIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : ids.length >= MAX_PHOTOS ? ids : [...ids, id]));

  const canSubmit = topic.trim().length >= 2 && !submit.isPending;

  return (
    <Card data-testid="card-topic-wish-form">
      <CardHeader>
        <CardTitle className="text-lg">Themenwunsch hinzufügen</CardTitle>
        <CardDescription>Ihr Team bekommt Ihren Wunsch sofort und plant ihn in Ihre Beiträge ein.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (canSubmit) submit.mutate(); }}
        >
          <div className="space-y-1">
            <label htmlFor="wish-topic" className="text-sm font-medium">Thema</label>
            <Input
              id="wish-topic" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={200}
              placeholder="Zum Beispiel: Martinsgans-Menü im Restaurant" list="wish-topic-suggestions"
              data-testid="input-wish-topic"
            />
            <datalist id="wish-topic-suggestions">{topicNames.map((n) => <option key={n} value={n} />)}</datalist>
          </div>
          <div className="space-y-1">
            <label htmlFor="wish-period" className="text-sm font-medium">Zeitraum (optional)</label>
            <Input
              id="wish-period" value={period} onChange={(e) => setPeriod(e.target.value)} maxLength={200}
              placeholder="Zum Beispiel: 1. bis 15. November" data-testid="input-wish-period"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="wish-note" className="text-sm font-medium">Notiz (optional)</label>
            <Textarea
              id="wish-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
              placeholder="Was ist Ihnen wichtig? Preise, Ansprechpartner, Besonderheiten" className="min-h-[80px]"
              data-testid="textarea-wish-note"
            />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">Fotos aus Ihrer Bibliothek (optional, bis zu {MAX_PHOTOS})</div>
            {photos.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : photoList.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Fotos in Ihrer Post-Bibliothek.</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2" data-testid="wish-photo-picker">
                {photoList.map((p) => {
                  const selected = photoIds.includes(p.id);
                  return (
                    <button
                      type="button" key={p.id} onClick={() => togglePhoto(p.id)}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 ${selected ? "border-primary" : "border-transparent"}`}
                      aria-pressed={selected} aria-label={`Foto ${p.originalName} ${selected ? "abwählen" : "auswählen"}`}
                      data-testid={`wish-photo-${p.id}`}
                    >
                      <img src={p.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                      {selected && (
                        <span className="absolute top-1 right-1 rounded-full bg-primary text-primary-foreground p-0.5">
                          <Check className="h-3 w-3" aria-hidden="true" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!canSubmit} data-testid="button-submit-topic-wish">
              {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
              Wunsch senden
            </Button>
            <Button type="button" variant="outline" onClick={onDone}>Abbrechen</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
